import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import useToken from '@/shared/hooks/useToken';
import { AppLayout } from '../layout/AppLayout';

// Lazy load route components for code splitting
// Dashboard is migrated, others will be migrated in subsequent steps
const Dashboard = lazy(() => import('@/features/dashboard/page'));
const Settings = lazy(() => import('@/features/settings'));
const Accounts = lazy(() => import('@/features/accounts'));
const Automations = lazy(() => import('@/features/automations'));
const Insights = lazy(() => import('@/features/insights'));
const ScheduledContent = lazy(() => import('@/features/scheduled-content'));
const Messages = lazy(() => import('@/features/messages'));
const CreateScheduledContent = lazy(() => import('@/features/scheduled-content/pages/create'));
const CreateAutomation = lazy(() => import('@/features/automations/pages/create'));
const UpdateAutomation = lazy(() => import('@/features/automations/pages/update'));
const Plans = lazy(() => import('@/features/plans'));
const NoMatch = lazy(() => import('@/features/no-match'));
const SignIn = lazy(() => import('@/features/auth/pages/sign-in'));
const SignUp = lazy(() => import('@/features/auth/pages/sign-up'));
const ForgotPassword = lazy(() => import('@/features/auth/pages/forgot-password'));
const ResetPassword = lazy(() => import('@/features/auth/pages/reset-password'));

const SuspenseFallback = ({ minHeight = '400px' }: { minHeight?: string }) => (
    <Box className="flex justify-center items-center" sx={{ minHeight }}>
        <CircularProgress sx={{ color: 'var(--color-primary)' }} />
    </Box>
);

export const AppRoutes = () => {
    const { token } = useToken();

    return (
        <Suspense fallback={<SuspenseFallback minHeight="100vh" />}>
            <Routes>
                {token ? (
                    <>
                        <Route
                            path="/"
                            element={
                                <AppLayout>
                                    <Suspense fallback={<SuspenseFallback />}>
                                        <Dashboard />
                                    </Suspense>
                                </AppLayout>
                            }
                        />
                        <Route path="/dashboard" element={<Navigate to="/" />} />
                        <Route path="sign-in" element={<Navigate to="/" />} />
                        <Route path="sign-up" element={<Navigate to="/" />} />
                        <Route
                            path="settings"
                            element={
                                <AppLayout>
                                    <Suspense fallback={<SuspenseFallback />}>
                                        <Settings />
                                    </Suspense>
                                </AppLayout>
                            }
                        />
                        <Route
                            path="accounts"
                            element={
                                <AppLayout>
                                    <Suspense fallback={<SuspenseFallback />}>
                                        <Accounts />
                                    </Suspense>
                                </AppLayout>
                            }
                        />
                        <Route
                            path="automations"
                            element={
                                <AppLayout>
                                    <Suspense fallback={<SuspenseFallback />}>
                                        <Automations />
                                    </Suspense>
                                </AppLayout>
                            }
                        />
                        <Route
                            path="insights"
                            element={
                                <AppLayout>
                                    <Suspense fallback={<SuspenseFallback />}>
                                        <Insights />
                                    </Suspense>
                                </AppLayout>
                            }
                        />
                        <Route
                            path="scheduled-content"
                            element={
                                <AppLayout>
                                    <Suspense fallback={<SuspenseFallback />}>
                                        <ScheduledContent />
                                    </Suspense>
                                </AppLayout>
                            }
                        />
                        <Route
                            path="messages"
                            element={
                                <AppLayout>
                                    <Suspense fallback={<SuspenseFallback />}>
                                        <Messages />
                                    </Suspense>
                                </AppLayout>
                            }
                        />
                        <Route
                            path="scheduled-content/create"
                            element={
                                <AppLayout>
                                    <Suspense fallback={<SuspenseFallback />}>
                                        <CreateScheduledContent />
                                    </Suspense>
                                </AppLayout>
                            }
                        />
                        <Route
                            path="automations/create"
                            element={
                                <AppLayout>
                                    <Suspense fallback={<SuspenseFallback />}>
                                        <CreateAutomation />
                                    </Suspense>
                                </AppLayout>
                            }
                        />
                        <Route
                            path="automations/update/:id"
                            element={
                                <AppLayout>
                                    <Suspense fallback={<SuspenseFallback />}>
                                        <UpdateAutomation />
                                    </Suspense>
                                </AppLayout>
                            }
                        />
                        <Route
                            path="plans"
                            element={
                                <AppLayout>
                                    <Suspense fallback={<SuspenseFallback />}>
                                        <Plans />
                                    </Suspense>
                                </AppLayout>
                            }
                        />
                        <Route
                            path="*"
                            element={
                                <AppLayout>
                                    <Suspense fallback={<SuspenseFallback />}>
                                        <NoMatch />
                                    </Suspense>
                                </AppLayout>
                            }
                        />
                    </>
                ) : (
                    <>
                        <Route index element={<SignIn />} />
                        <Route path="sign-in" element={<Navigate to="/" />} />
                        <Route path="sign-up" element={<SignUp />} />
                        <Route path="forgot-password/" element={<ForgotPassword />} />
                        <Route path="forgot-password/confirm/:var" element={<ResetPassword />} />
                        <Route path="*" element={<NoMatch />} />
                    </>
                )}
            </Routes>
        </Suspense>
    );
};
